import { useCallback, useEffect, useRef, useState } from 'react';
import { getAppleClientId, getAppleRedirectUri, APPLE_ID_AUTH_SCRIPT_URL } from '../config';
import { APPLE_USER_CANCELLED, generateRandomString, parseAppleAuthResponse } from '../helpers/appleAuth';

let scriptLoadPromise = null;

const loadAppleScript = () => {
  if (window.AppleID?.auth) {return Promise.resolve();}
  if (scriptLoadPromise) {return scriptLoadPromise;}
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = APPLE_ID_AUTH_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later mount try again instead of caching the failure for the whole page.
      scriptLoadPromise = null;
      script.remove();
      reject(new Error('Failed to load Sign in with Apple script'));
    };
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
};

// Closing the popup window is the same kind of cancellation as pressing Cancel in it - like
// Google's popup_closed, the form just stays as-is. user_trigger_new_signin_flow is the first
// attempt being superseded when the customer clicks the button again.
const CANCEL_ERRORS = [APPLE_USER_CANCELLED, 'popup_closed_by_user', 'user_trigger_new_signin_flow'];

// Wraps Sign in with Apple JS (APPLE_ID_AUTH_SCRIPT_URL) in popup mode, for prefill only - no
// EarthLink session or account is created and authorization.code is never used.
//
// usePopup: true is required: without it Apple redirects the whole page and the checkout state
// (the redux store is not persisted) is lost. AppleID.auth.signIn() is called synchronously
// from the button's click handler with nothing awaited before it - otherwise the browser no
// longer treats it as a user gesture and blocks the popup.
const useAppleSignIn = ({ onSuccess, onError } = {}) => {
  const clientId = getAppleClientId();
  const redirectURI = getAppleRedirectUri();
  const isConfigured = Boolean(clientId && redirectURI);
  const [isReady, setIsReady] = useState(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!isConfigured) {return undefined;}
    let cancelled = false;

    // A load failure is not reported here - the customer hasn't asked for Apple yet. It
    // surfaces as the inline error if they click the button while it isn't ready.
    loadAppleScript()
      .then(() => {
        if (!cancelled && window.AppleID?.auth) {setIsReady(true);}
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isConfigured]);

  const triggerSignIn = useCallback(() => {
    if (!isConfigured) {return;}
    if (!isReady || !window.AppleID?.auth) {
      onErrorRef.current?.(new Error('Sign in with Apple is not ready'));
      return;
    }

    // New state and nonce for every attempt; a response carrying any other values is ignored.
    const state = generateRandomString();
    const nonce = generateRandomString();
    let signInPromise;
    try {
      window.AppleID.auth.init({
        clientId,
        scope: 'name email',
        redirectURI,
        state,
        nonce,
        usePopup: true,
      });
      signInPromise = window.AppleID.auth.signIn();
    } catch (error) {
      onErrorRef.current?.(error);
      return;
    }

    Promise.resolve(signInPromise)
      .then((response) => {
        const profile = parseAppleAuthResponse(response, { expectedState: state, expectedNonce: nonce });
        if (profile) {onSuccessRef.current?.(profile);}
      })
      .catch((error) => {
        if (CANCEL_ERRORS.includes(error?.error)) {return;}
        onErrorRef.current?.(error);
      });
  }, [clientId, redirectURI, isConfigured, isReady]);

  return {
    isConfigured,
    isReady,
    triggerSignIn,
  };
};

export default useAppleSignIn;
